import { isNil, isEmpty, pathOr } from 'ramda'

import { fetchPrecheckedProviders, resetPrecheckedProviders } from 'store/Care'
import { fetchPointSolutions } from 'store/PointSolutions'
import { addContentfulCareSearchEvent } from 'utils/heap/addContentfulCareSearchEvent'
import { addPreCheckedProviderEvent } from 'utils/heap/addPreCheckedProviderEvent'
import { isActionRelevant } from './utils'
import { getPreCheckedProvidersForCodeType } from 'store/Care/utils/getPreCheckedProvidersForCodeType'
import { parseCareDetail } from 'store/Care/utils/parseCareDetail'
import { isStaleResponse } from 'store/Care/utils/isStaleResponse'

/**
 * Makes sequenced calls to prechecked providers and point solutions after the
 * contentful call. Utilizes meta from fetchCareContentfulData for both
 * fetchPrecheckedProviders and fetchPointSolutions.
 *
 * Flow:
 *
 * 1. FETCH_CARE_SEARCH_DATA_SUCCESS triggers this middleware
 * 2. Extracts categoryNames from contentful preCheckedProviderCategories
 * 3. Dispatches fetchPrecheckedProviders(categoryNames, insuranceProduct,
 *    ratingArea)
 * 4. Dispatches fetchPointSolutions using same categoryNames params for
 *    consistency
 *
 * TODO:LIGHTS-3917 - add point solutions fetch here
 */
export const getCareSearchPreCheckProviders =
  ({ dispatch, getState }) =>
  next =>
  action => {
    // get the insurance filing uuid from the policy for Point Solutions fetch
    const { Policy } = getState()
    const insuranceFilingUuid = Policy.policy?.entity?.insuranceFilingUuid

    const relevantActions = /FETCH_CARE_SEARCH_DATA_SUCCESS/
    if (!isActionRelevant(action, relevantActions)) return next(action)
    const careCategories = action.payload.data?.preCheckedCategories
    const insuranceProduct = action.meta?.insuranceProduct
    const ratingArea = action.meta?.ratingArea

    if (
      isNil(careCategories) ||
      isEmpty(careCategories?.categories) ||
      isEmpty(action.meta)
    ) {
      dispatch(resetPrecheckedProviders())
    } else {
      // Contentful data path for prechecked provider categories
      // payload.data.preCheckedCategories.categories.preCheckedProviderCategories

      const categoryNames = careCategories.categories
        .flatMap(category => category.preCheckedProviderCategories)
        .join(',')

      if (categoryNames.length) {
        dispatch(
          fetchPrecheckedProviders(categoryNames, insuranceProduct, ratingArea),
        )
      }

      // Point Solutions fetch
      if (insuranceFilingUuid) {
        // Check for a point solution category
        const pointSolutionCategories = careCategories.categories
          .filter(category => category.isPointSolution)
          .flatMap(category => category.preCheckedProviderCategories ?? [])

        // Only dispatch if there are Point Solution categories
        if (pointSolutionCategories.length) {
          // Only fetch the first category because the BE only accepts one
          dispatch(
            fetchPointSolutions({
              insuranceFilingUuid,
              category: pointSolutionCategories[0],
            }),
          )
        }
      }
    }

    return next(action)
  }

/**
 * Creates a Heap custom event for care search Contentful data data includes:
 * pre-checked provider categories, care guides, common questions and provider
 * guides
 */
export const addPreCheckedCareSearchHeapEvent = () => next => action => {
  const relevantActions = /FETCH_CARE_SEARCH_DATA_SUCCESS/
  if (!isActionRelevant(action, relevantActions)) return next(action)

  const contenfulData = action.payload
  const ratingArea = action.meta?.ratingArea
  const insuranceProduct = action.meta?.insuranceProduct
  const searchTerms = action.meta?.searchTerms

  // add a custom heap event for care search contentful data
  if (!isNil(contenfulData.data?.preCheckedCategories)) {
    addContentfulCareSearchEvent({
      careCategories:
        contenfulData.data.preCheckedCategories?.categories?.[0]
          ?.preCheckedProviderCategories,
      careGuide: contenfulData.data.tileCareGuideCollection?.careGuide,
      commonQuestions: contenfulData.data.commonQuestionCollection?.questions,
      insuranceProduct: insuranceProduct,
      providerGuide:
        contenfulData.data.tileProviderGuideCollection?.providerGuides,
      searchTerms: searchTerms,
      ratingArea: ratingArea,
    })
  }

  return next(action)
}

/**
 * Creates a Heap custom event for pre-checked providers returned from the care
 * detail endpoint
 */
export const addPreCheckedProviderHeapEvent = () => next => action => {
  const relevantActions = /FETCH_(CARE_DETAIL|PRECHECKED_PROVIDERS)_SUCCESS/
  if (!isActionRelevant(action, relevantActions)) return next(action)

  const payload = action.payload

  // if there is not a payload, continue to the next action without creating a heap event
  if (isNil(payload)) {
    return next(action)
  }

  // A detail response the reducers discard as superseded must not produce an
  // event either, or Heap records a care item that never reached the screen
  // (BASH-219). `action.state` is the store as of dispatch, added by
  // `addStateToAction`, which runs ahead of this middleware.
  if (
    action.type === 'careDetail/FETCH_CARE_DETAIL_SUCCESS' &&
    isStaleResponse(
      action.meta?.requestId,
      action.state?.Care?.detail?.requestId,
    )
  ) {
    return next(action)
  }

  let insuranceProduct = ''
  let preCheckedProviders = []
  let ratingArea = ''
  let searchTerms = ''

  // parse the payload for care detail actions
  if (action.type === 'careDetail/FETCH_CARE_DETAIL_SUCCESS') {
    searchTerms = pathOr('', ['name'], payload)
    // Narrow to the providers pre-checked for this code's type so the event
    // counts what the page actually shows
    preCheckedProviders = getPreCheckedProvidersForCodeType(
      payload?.precheckedProviders,
    )
    insuranceProduct = pathOr('', ['memberInfo', 'insuranceProduct'], payload)
    ratingArea = pathOr('', ['memberInfo', 'ratingArea'], payload)
  }

  // parse the payload for the care search prechecked providers actions
  if (action.type === 'careSearch/FETCH_PRECHECKED_PROVIDERS_SUCCESS') {
    insuranceProduct = action.meta?.insuranceProduct
    preCheckedProviders = payload
    ratingArea = action.meta?.ratingArea
    searchTerms = action.meta?.searchTerms || action.meta?.categoryNames
  }

  if (searchTerms && ratingArea && insuranceProduct) {
    addPreCheckedProviderEvent({
      insuranceProduct,
      preCheckedProviders,
      ratingArea,
      searchTerms,
    })
  }

  return next(action)
}

/**
 * This thunk will parse the care detail early in the flow so that by the time
 * it gets to any reducers, they all get the same, parsed result and don't need
 * to do their own parsing.
 */
export const preParseCareDetail = () => next => action => {
  const relevantActions = /FETCH_CARE_(DETAIL|EPISODE)_SUCCESS/
  if (!isActionRelevant(action, relevantActions)) return next(action)

  const parsedCareDetail = parseCareDetail(action.payload || {})

  return next({
    ...action,
    payload: parsedCareDetail,
  })
}
